/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */

// Check if we should use the Open Access Dashboard UI.
const USE_OPEN_ACCESS_DASHBOARD = process.env.VUE_APP_USE_OPEN_ACCESS_DASHBOARD == 'true';
console.log('Using Strata Open Access Dashboard:', USE_OPEN_ACCESS_DASHBOARD);

import Vue from 'vue'
import App from './App.vue'
import AppDashboard from './AppDashboard.vue'
import store from "@/store/index";

import 'vue-swatches/dist/vue-swatches.css'
import 'vue-material/dist/vue-material.min.css'
import './theme.scss'
import './blocklyDatePicker.scss'

import { AuthService } from './services/auth.service';
import { AnalyticsService } from "./services/analytics.service";
import { ConfigService } from "./services/config.service";
import { DatasetService } from "./services/dataset.service";
import {
    MdApp, MdToolbar, MdButton, MdMenu, MdIcon, MdContent,
    MdList, MdAvatar, MdField, MdLayout, MdSnackbar, MdTabs, MdDialog, MdDialogAlert,
    MdDialogPrompt, MdDialogConfirm, MdDrawer, MdChips, MdCheckbox, MdCard, MdSubheader, MdTooltip,
    MdTable, MdRadio, MdRipple, MdAutocomplete, MdProgress, MdSwitch, MdSteppers, MdDivider, MdBadge, MdEmptyState,
} from 'vue-material/dist/components'
import { GA_EVENTS } from './constants/gaConstants'
import './toolbox_label'
import './toolbox_cta'
import './blocklyTheme'
import './colours_renderer'
import './warning'
import './comment'
import { initialiseDataset } from '@/blocks/dataset'
import router from "./router";

// Init a bunch of services asynchronously
(async () => {

    AnalyticsService.init();

    /**
     * Initialize remaining services only if using Earth Blox Builder UI.
     */
    if (!USE_OPEN_ACCESS_DASHBOARD) {
        AuthService.init();
        await ConfigService.init();
        // TODO Only import/init DataSelector if it is required, i.e. the user has access to output_data_selector
        await DatasetService.init();
        await initialiseDataset();
    }

})().then(()=>{


    Vue.use(MdApp);
    Vue.use(MdToolbar);
    Vue.use(MdButton);
    Vue.use(MdMenu);
    Vue.use(MdDialogAlert);
    Vue.use(MdIcon);
    Vue.use(MdContent);
    Vue.use(MdList);
    Vue.use(MdAvatar);
    Vue.use(MdField);
    Vue.use(MdLayout);
    Vue.use(MdSnackbar);
    Vue.use(MdTabs);
    Vue.use(MdDialog);
    Vue.use(MdDialogPrompt);
    Vue.use(MdDialogConfirm);
    Vue.use(MdDrawer);
    Vue.use(MdChips);
    Vue.use(MdCheckbox);
    Vue.use(MdCard);
    Vue.use(MdSubheader);
    Vue.use(MdTooltip);
    Vue.use(MdTable);
    Vue.use(MdRadio);
    Vue.use(MdRipple);
    Vue.use(MdAutocomplete);
    Vue.use(MdProgress);
    Vue.use(MdSwitch);
    Vue.use(MdSteppers);
    Vue.use(MdDivider);
    Vue.use(MdBadge);
    Vue.use(MdEmptyState)

    /**
     * Fix error in MdSlect.
     * See: https://github.com/vuematerial/vue-material/issues/2285#issuecomment-1059410143
     */
    Vue.component('MdSelect', Vue.options.components.MdSelect.extend({
        methods: {
            isInvalidValue: function isInvalidValue() {
                return this.$el.validity ? this.$el.validity.badInput : this.$el.querySelector('input').validity.badInput
            }
        }
    }))


    Vue.config.productionTip = false
    //Add unimported components to ignore list to prevent warnings.
    Vue.config.ignoredElements = ['field', 'block', 'category', 'xml', 'mutation', 'value', 'sep']

    // Add global mixins to make methods and data available to all components.
    //   > Use with caution! Once you apply a mixin globally, it will affect every
    //   > Vue instance created afterwards.
    // See https://vuejs.org/v2/guide/mixins.html#Global-Mixin
    // Note these are namespaced with "eb" to try and avoid clashes.
    // Note if we move to TypeScript, we need to do this in a typesafe way via https://www.npmjs.com/package/vue-typed-mixins
    Vue.mixin({
        methods: {
            /**
             * Simply log a message to the console at debug level.
             * This can be useful to log to console from e.g. click events
             * in the HTML of a component, which don't have access to the console.
             * @param {string} msg
             */
            ebxDebug(msg) {
                console.debug(msg);
            },
            /**
             * Log an analytics event. This is to support simple logging of analytics
             * events from e.g. click events in the HTML of a component, which don't
             * have access to the imported methods in a Vue component.
             * @param {string} eventName
             * @param {number} eventValue
             * @param {object} extraProps
             */
            ebxLogAnalytics(eventName, eventValue, extraProps) {
                AnalyticsService.logAnalyticsEvent(eventName, eventValue, extraProps);
            },
        },
        // Similarly, provide access to constants via component data and option merging.
        // See https://vuejs.org/v2/guide/mixins.html#Option-Merging
        data: function () {
            return {
                // Provide access to GA_EVENTS constants in HTML element event handling code
                GA_EVENTS: GA_EVENTS,
                // should components use the open access dashboard
                ebxUseOpenAccessDashboard: USE_OPEN_ACCESS_DASHBOARD
            }
        },
        filters: {
            json_pp: function (value) {
                try {
                    return value ? JSON.stringify(JSON.parse(value), null, 2) : "";
                } catch {
                    return value ? JSON.stringify(value, null, 2) : "";
                }
            },
            capitalize: function (value) {
                if (!value) return ''
                value = value.toString()
                return value.charAt(0).toUpperCase() + value.slice(1)
            },
        }
    });

    // switch UI if Open Access Dashboard is enabled.
    const RootApp = USE_OPEN_ACCESS_DASHBOARD ? AppDashboard : App;

    console.info("Initialising Vue app")
    new Vue({
        router,
        store,
        render: (h) => h(RootApp),
    }).$mount("#app");
})